import { getRouteApi } from '@tanstack/react-router';
import { ContextMenu, type ContextMenuItem } from 'cpool-components/general/ContextMenu';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { APPLICANT_ATTRIBUTIONS } from 'cpool-constants';
import { createId } from 'cpool-utils';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';

import { expenseAddButtonOrder, type ExpenseType, expenseTypeConfig } from './expenseTypeConfig.ts';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaExpensesForm } from '~/features/leadDetails/schemas/expenses.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    applicant: 1 | 2;
};

export const AddExpense: React.FC<Props> = ({ applicant }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control } = useFormContext<SchemaExpensesForm>();
    const { registerAddedRow } = useLeadDetailsFormContext();
    const { t } = useTranslation();
    const { append: appendRentalExpense } = useFieldArray({ control, name: 'mietausgaben' });
    const { append: appendHealthInsuranceExpense } = useFieldArray({
        control,
        name: 'privateKrankenversicherungen',
    });
    const { append: appendOtherExpense } = useFieldArray({ control, name: 'sonstigeAusgaben' });
    const { append: appendSupportExpense } = useFieldArray({ control, name: 'unterhaltsverpflichtungen' });
    const attribution =
        applicant === 2 ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2 : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1;

    const appendExpense = (type: ExpenseType) => {
        const expense = {
            betragMonatlich: 0,
            gehoertZuAntragsteller: attribution,
            id: createId(),
        };

        switch (type) {
            case 'mietausgaben':
                appendRentalExpense(expense);
                break;
            case 'privateKrankenversicherungen':
                appendHealthInsuranceExpense(expense);
                break;
            case 'sonstigeAusgaben':
                appendOtherExpense(expense);
                break;
            case 'unterhaltsverpflichtungen':
                appendSupportExpense(expense);
                break;
        }

        registerAddedRow(expense.id);
    };
    const menuItems: ContextMenuItem[] = expenseAddButtonOrder.map((type) => ({
        disabled: !canExecuteApplicantCommand({
            applicant,
            commandName: expenseTypeConfig[type].addCommand,
            permissions,
        }),
        id: type,
        label: t(expenseTypeConfig[type].label),
        onClick: () => appendExpense(type),
    }));

    return (
        <ContextMenu
            items={menuItems}
            panelWidth={280}
            trigger={
                <Button
                    ariaLabel={t('EXPENSES.ADD')}
                    disabled={menuItems.every((item) => item.disabled)}
                    className={'h-8 w-8 shrink-0'}
                    iconLeft={<LuPlus size={14} />}
                    type="button"
                    variant={ButtonVariant.SECONDARY}
                />
            }
        />
    );
};
